import { Component, Inject, OnInit } from '@angular/core';
import { MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';
import { MatSnackBar } from '@angular/material/snack-bar';
import { KontrollterminGenerationService } from '../../../services/kontrolltermin-generation.service';
import { MitarbeiterService } from '../../../services/mitarbeiter.service';
import { environment } from '../../../../environments/environment';

export interface EditKontrollterminData {
  kontrolltermin: any;
  standortId: string;
}

@Component({
  selector: 'app-edit-kontrolltermin-dialog',
  templateUrl: './edit-kontrolltermin-dialog.component.html',
  styleUrls: ['./edit-kontrolltermin-dialog.component.scss']
})
export class EditKontrollterminDialogComponent implements OnInit {
  isLoading = false;
  isSaving = false;
  
  // Available options
  technikerOptions: any[] = [];
  statusOptions: any[] = [];
  
  // ngModel properties
  selectedTechnikerId: string = '';
  selectedStatusId: string = '';
  
  // Environment for template access
  environment = environment;

  constructor(
    private dialogRef: MatDialogRef<EditKontrollterminDialogComponent>,
    @Inject(MAT_DIALOG_DATA) public data: EditKontrollterminData,
    private kontrollterminService: KontrollterminGenerationService,
    private mitarbeiterService: MitarbeiterService,
    private snackBar: MatSnackBar
  ) {
  }

  ngOnInit(): void {
    this.loadOptions();
  }

  private loadOptions(): void {
    this.isLoading = true;
    
    // Load status options from environment first
    this.statusOptions = environment.kontrolltermin_status || [];
    
    // Load techniker options
    this.mitarbeiterService.getTechnicians().subscribe({
      next: (response) => {
        this.technikerOptions = response.data || [];
        this.isLoading = false;
        // Populate form after techniker options are loaded
        this.populateForm();
      },
      error: (error) => {
        console.error('Error loading techniker:', error);
        this.snackBar.open('Fehler beim Laden der Techniker', 'Schließen', { duration: 3000 });
        this.isLoading = false;
        // Still populate form even if techniker loading fails
        this.populateForm();
      }
    });

    // Fallback: populate form after a timeout to ensure it's called even if API fails
    setTimeout(() => {
      if (this.isLoading) {
        this.isLoading = false;
        this.populateForm();
      }
    }, 2000);
  }

  private populateForm(): void {
    const kt = this.data.kontrolltermin;
    
    // Set ngModel values directly
    let formTechnikerId = kt.techniker_id || '';
    let foundTechniker: any = null;
    
    // Handle data type mismatch: kontrolltermin has string ID, techniker options have numeric ID
    if (formTechnikerId && this.technikerOptions.length > 0) {
      // Convert string ID to number for comparison
      const numericId = parseInt(formTechnikerId, 10);
      
      foundTechniker = this.technikerOptions.find(t => 
        t.id === numericId || 
        t.id === formTechnikerId ||
        t.id === formTechnikerId.toString()
      );
      
      if (foundTechniker) {
        formTechnikerId = foundTechniker.id.toString();
      }
    }
    
    // Set ngModel values - use the actual techniker ID (number) for mat-select
    this.selectedTechnikerId = foundTechniker ? foundTechniker.id : formTechnikerId;
    
    // Handle status ID - check if it matches any status option
    let statusId = kt.status_id || '';
    
    // Find matching status option
    const foundStatus = this.statusOptions.find(s => 
      s.id === statusId || 
      s.id === parseInt(statusId, 10) ||
      s.id === statusId.toString()
    );
    
    if (foundStatus) {
      this.selectedStatusId = foundStatus.id;
    } else {
      this.selectedStatusId = statusId;
    }
  }

  onSave(): void {
    // Validate ngModel values
    if (!this.selectedTechnikerId || !this.selectedStatusId) {
      this.snackBar.open('Bitte füllen Sie alle Felder aus', 'Schließen', { duration: 3000 });
      return;
    }
    
    this.isSaving = true;
    const formData = {
      techniker_id: this.selectedTechnikerId,
      status_id: this.selectedStatusId
    };
    
    // Call the API to update the kontrolltermin
    this.kontrollterminService.updateKontrolltermin(this.data.kontrolltermin.id, formData).subscribe({
      next: (response) => {
        // Find the updated techniker and status names
        const updatedTechniker = this.technikerOptions.find(t => t.id == this.selectedTechnikerId);
        const updatedStatus = this.statusOptions.find(s => s.id == this.selectedStatusId);
        
        const updatedKontrolltermin = {
          ...this.data.kontrolltermin,
          techniker_id: this.selectedTechnikerId,
          status_id: this.selectedStatusId,
          techniker_name: updatedTechniker ? updatedTechniker.display_name : this.data.kontrolltermin.techniker_name,
          status_name: updatedStatus ? updatedStatus.label : this.data.kontrolltermin.status_name,
          status_badge_class: this.getStatusBadgeClass(this.selectedStatusId)
        };
        
        this.snackBar.open('Kontrolltermin erfolgreich aktualisiert', 'Schließen', { duration: 3000 });
        this.dialogRef.close(updatedKontrolltermin);
        this.isSaving = false;
      },
      error: (error) => {
        console.error('Error updating kontrolltermin:', error);
        this.snackBar.open('Fehler beim Aktualisieren des Kontrolltermins', 'Schließen', { duration: 5000 });
        this.isSaving = false;
      }
    });
  }

  onCancel(): void {
    this.dialogRef.close();
  }

  getTechnikerName(technikerId: string): string {
    // Handle data type mismatch: technikerId is string, but techniker options have numeric IDs
    const numericId = parseInt(technikerId, 10);
    
    const techniker = this.technikerOptions.find(t => 
      t.id === numericId || 
      t.id === technikerId ||
      t.id === technikerId.toString()
    );
    
    return techniker ? techniker.display_name : 'Unbekannt';
  }

  getStatusLabel(statusId: number | string): string {
    const id = typeof statusId === 'string' ? parseInt(statusId, 10) : statusId;
    const status = this.statusOptions.find(s => s.id === id);
    return status ? status.label : 'Unbekannt';
  }

  getStatusBadgeClass(statusId: number | string): string {
    const id = typeof statusId === 'string' ? parseInt(statusId, 10) : statusId;
    const classMap: {[key: number]: string} = {
      1: 'bg-red-200',      // geloescht
      2: 'bg-orange-200',   // abgebrochen
      3: 'bg-red-200',      // fehlerhaft
      4: 'bg-blue-200',     // geplant
      5: 'bg-yellow-200',   // aktuell
      6: 'bg-yellow-200',   // in_bearbeitung
      7: 'bg-purple-200',   // freigabe
      8: 'bg-green-200'     // abgeschlossen
    };
    return classMap[id] || 'bg-gray-200';
  }
}